import { useEffect, useRef, useState } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { Select } from '../../../../components/inputs/Select';
import { personnelApi, personsApi } from '../../../human-resources/personnel/personnelApi';

/** One employee offered or chosen. The label is their name; the number tells two namesakes apart. */
export interface PlanEmployee {
  /** The personnel id — what the plan is written against. */
  id: string;
  name: string;
  employeeNumber: string;
}

/**
 * Who a development plan is for — SEARCH, THEN A TABLE (the Share dialog's shape): choosing an
 * employee ADDS A ROW, and each row is taken back off with its own Remove.
 *
 * It offers EVERY employee (product call, 13 Oct 2026), not only the survey's assessed
 * respondents: one who never answered can be put on a plan too, and is then on the plan WITHOUT
 * counting as a response. The server decides the rest, all or none — nobody above the reader's
 * access level, and nobody who already has a plan in this survey; a refusal names the ROW.
 *
 * Names are resolved in batches and never stored. **The search reaches a NAME only among the
 * employees already loaded; beyond those it reaches the EMPLOYEE NUMBER** — a name lives in a
 * store the server cannot search. A row above the reader's access level is not offered.
 *
 * Reading the employee list is `personnel.read`; without it the picker says so.
 */
export function PlanEmployeePicker({
  idPrefix,
  value,
  onChange,
  onOffered,
  disabled,
  error,
}: {
  idPrefix: string;
  value: PlanEmployee[];
  onChange: (next: PlanEmployee[]) => void;
  /** The employees loaded so far — what Autogenerate picks from. */
  onOffered?: (offered: PlanEmployee[]) => void;
  disabled: boolean;
  error?: string;
}) {
  const canRead = usePermission('personnel.read');

  const [offered, setOffered] = useState<PlanEmployee[]>([]);
  const [loading, setLoading] = useState(canRead);
  const [query, setQuery] = useState('');

  // The latest callback, so the fetch effect does not re-run when a parent re-renders.
  const offeredRef = useRef(onOffered);
  useEffect(() => {
    offeredRef.current = onOffered;
  }, [onOffered]);

  useEffect(() => {
    if (!canRead) return;

    const controller = new AbortController();

    // Typing waits a moment before it asks; the first load does not.
    const timer = window.setTimeout(
      () => {
        personnelApi
          .list({ pageSize: 100, status: 'active', search: query || undefined }, controller.signal)
          .then(async (page) => {
            const rows = page.items.filter((r) => r.personId != null);
            const ids = rows.map((r) => r.personId as string);
            const names = ids.length > 0 ? await personsApi.resolve(ids, controller.signal).catch(() => []) : [];
            const byPerson = new Map(names.map((n) => [n.personId, n.displayName]));

            if (controller.signal.aborted) return;

            const fetched = rows.map((r) => ({
              id: r.id,
              name: byPerson.get(r.personId as string) ?? r.employeeNumber,
              employeeNumber: r.employeeNumber,
            }));

            // MERGED, never replaced: an employee found by an earlier search stays searchable by name.
            setOffered((current) => {
              const known = new Set(current.map((e) => e.id));
              const next = [...current, ...fetched.filter((e) => !known.has(e.id))];

              offeredRef.current?.(next);

              return next;
            });
          })
          .catch(() => undefined)
          .finally(() => {
            if (!controller.signal.aborted) setLoading(false);
          });
      },
      query ? 250 : 0,
    );

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [canRead, query]);

  const chosen = new Set(value.map((e) => e.id));

  return (
    <div className="flex flex-col gap-2">
      <Select
        id={`${idPrefix}-personnel-ids`}
        label="Employees"
        description={
          canRead
            ? 'Any employee, whether or not they answered this survey. Search by name, or by employee number for one not listed yet.'
            : 'Choosing employees needs permission to read personnel records.'
        }
        placeholder="Search employees to add…"
        // Never holds a value: choosing one adds a row below and the box is ready for the next.
        value=""
        onChange={(next) => {
          const picked = offered.find((e) => e.id === next);

          if (picked && !chosen.has(picked.id)) onChange([...value, picked]);
        }}
        onQueryChange={setQuery}
        options={offered
          .filter((e) => !chosen.has(e.id))
          .map((e) => ({
            value: e.id,
            label: e.name,
            // Suppressed where the number IS the label — an unresolved name falls back to it.
            sublabel: e.name === e.employeeNumber ? undefined : e.employeeNumber,
            keywords: e.employeeNumber,
          }))}
        remote
        loading={loading}
        disabled={disabled || !canRead}
        error={error}
      />

      <div className="modern-scrollbar overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full table-auto">
          <thead className="bg-slate-100">
            <tr className="text-[9px] uppercase tracking-wider text-gray-600 [&>th]:font-normal">
              <th className="w-12 px-3 py-2 text-left">#</th>
              <th className="border-l border-slate-200 px-3 py-2 text-left">Employee</th>
              <th className="w-44 border-l border-slate-200 px-3 py-2 text-left">Employee No.</th>
              <th className="w-28 border-l border-slate-200 px-3 py-2 text-right">Action</th>
            </tr>
          </thead>

          <tbody>
            {value.length === 0 ? (
              <tr className="border-t border-slate-100">
                <td colSpan={4} className="px-3 py-2 align-middle text-sm text-slate-400">
                  No employee added yet.
                </td>
              </tr>
            ) : (
              value.map((employee, index) => (
                <tr key={employee.id} className="border-t border-slate-100">
                  {/* Numbered, because a refusal names an employee by their row. */}
                  <td className="px-3 py-2 align-middle font-mono text-sm text-slate-500">{index + 1}</td>
                  <td className="border-l border-slate-100 px-3 py-2 align-middle text-sm text-slate-600">{employee.name}</td>
                  <td className="border-l border-slate-100 px-3 py-2 align-middle font-mono text-sm text-slate-500">
                    {employee.employeeNumber}
                  </td>
                  <td className="border-l border-slate-100 px-3 py-2 text-right align-middle">
                    <button
                      id={`${idPrefix}-personnel-ids-remove-${employee.id}`}
                      type="button"
                      className="text-xs font-medium text-red-600 hover:underline"
                      onClick={() => onChange(value.filter((e) => e.id !== employee.id))}
                      disabled={disabled}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
